import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-hot-toast';
import { HiMail, HiLockClosed, HiEye, HiEyeOff } from 'react-icons/hi';

import { loginUser } from '../../redux/actions/user';

import { validateEmail } from '../../common/validations';
import { capitalizarPalabras } from '../../common/upperCaseWord';

import { Button } from '../../components/buttons/Button';
import { ButtonLink } from '../../components/buttons/ButtonLink';
import { CopyrightFooter } from '../../components/copyrightFooter/CopyrightFooter';
import { BrandingLogo } from '../../components/brandingLogo/BrandingLogo';

export const FormLogin = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { loadingLogin } = useSelector((state) => state.user);

    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [focusedInput, setFocusedInput] = useState(null);
    const [emailError, setEmailError] = useState(null);
    const [passwordError, setPasswordError] = useState(null);
    const [formData, setFormData] = useState({
        email: '',
        password: ''
    });

    const onChange = (e) => {
        const { name, value } = e.target;
        if (name === 'email') {
            setEmailError(null);
        }
        if (name === 'password') {
            setPasswordError(null);
        }
        setFormData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
    }

    const validateForm = () => {
        const { email, password } = formData;
        const emailTrim = (email ?? '').trim();
        const emailValid = validateEmail(emailTrim);

        if (!emailTrim) {
            setEmailError('required');
            setPasswordError(null);
            toast.error('Ingresa tu correo electrónico');
            setLoading(false);
            return false;
        }
        if (!emailValid) {
            setEmailError('invalid');
            setPasswordError(null);
            toast.error('Ingresa un correo electronico valido');
            setLoading(false);
            return false;
        }
        setEmailError(null);
        if (!password) {
            setPasswordError('required');
            toast.error('Ingresa tu contraseña');
            setLoading(false);
            return false;
        }
        setPasswordError(null);
        return true;
    }

    const handleLogin = (e) => {
        e.preventDefault();
        setLoading(true);
        const { email, password } = formData;
        const isValid = validateForm();

        if (isValid) {
            dispatch(loginUser({ email: email.trim(), password }))
                .then((result) => {
                    if (result.status === 200) {
                        toast.success(capitalizarPalabras(result.message));
                        navigate('/');
                    } else {
                        toast.error(result.message);
                    }
                    setLoading(false);
                });
        }

    }

    return (
        <div className='w-full flex-1 min-h-0 h-full flex flex-col bg-white animate-fadeIn'>
            <div className='w-full max-w-md mx-auto flex flex-col flex-1 min-h-0 px-4 md:px-6'>
                <div className='flex-1 flex flex-col justify-center min-h-0 overflow-y-auto py-6'>
                    <div className='flex justify-center shrink-0 mb-8 animate-slideDown'>
                        <BrandingLogo />
                    </div>

                    <div className='text-center mb-8 animate-slideUp'>
                        <h2 className='text-2xl font-bold text-gray-800 mb-2'>¡Bienvenido de nuevo!</h2>
                        <p className='text-gray-500 text-sm'>Inicia sesión para continuar</p>
                    </div>

                    <form noValidate onSubmit={(e) => handleLogin(e)} className='space-y-6'>
                    {/* Email Input con icono */}
                    <div className='relative animate-slideUp' style={{ animationDelay: '0.1s' }}>
                        <label
                            htmlFor='login-email'
                            className={`block text-sm font-semibold mb-2 ${emailError ? 'text-red-700' : 'text-gray-700'}`}
                        >
                            Correo electrónico
                        </label>
                        <div className='relative'>
                            <div className={`absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none transition-colors duration-300 ${
                                emailError ? 'text-red-500' : focusedInput === 'email' ? 'text-indigo-600' : 'text-gray-400'
                            }`}>
                                <HiMail className='w-5 h-5' />
                            </div>
                            <input
                                id='login-email'
                                type='email'
                                name='email'
                                autoComplete='email'
                                value={formData.email}
                                onChange={onChange}
                                onFocus={() => setFocusedInput('email')}
                                onBlur={() => setFocusedInput(null)}
                                placeholder='tucorreo@ejemplo.com'
                                disabled={loading}
                                aria-invalid={emailError ? 'true' : 'false'}
                                className={`w-full pl-12 pr-4 py-3.5 border-2 rounded-xl transition-all duration-300 outline-none
                                    ${emailError
                                        ? 'border-red-500 bg-red-50 text-red-900 placeholder-red-300 focus:ring-4 focus:ring-red-100'
                                        : focusedInput === 'email'
                                            ? 'border-indigo-600 bg-indigo-50/50 shadow-lg shadow-indigo-200/50 focus:ring-4 focus:ring-indigo-100'
                                            : 'border-gray-200 bg-white hover:border-gray-300 focus:ring-4 focus:ring-indigo-100'
                                    }
                                    disabled:bg-gray-50 disabled:cursor-not-allowed`}
                            />
                        </div>
                        {emailError === 'required' && (
                            <p className='mt-2 text-sm text-red-600'>Ingresa tu correo electrónico.</p>
                        )}
                        {emailError === 'invalid' && (
                            <p className='mt-2 text-sm text-red-600'>El formato del correo no es válido.</p>
                        )}
                    </div>

                    {/* Password Input con toggle de visibilidad */}
                    <div className='relative animate-slideUp' style={{ animationDelay: '0.2s' }}>
                        <label
                            htmlFor='login-password'
                            className={`block text-sm font-semibold mb-2 ${passwordError ? 'text-red-700' : 'text-gray-700'}`}
                        >
                            Contraseña
                        </label>
                        <div className='relative'>
                            <div className={`absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none transition-colors duration-300 ${
                                passwordError ? 'text-red-500' : focusedInput === 'password' ? 'text-indigo-600' : 'text-gray-400'
                            }`}>
                                <HiLockClosed className='w-5 h-5' />
                            </div>
                            <input
                                id='login-password'
                                type={showPassword ? 'text' : 'password'}
                                name='password'
                                autoComplete='current-password'
                                value={formData.password}
                                onChange={onChange}
                                onFocus={() => setFocusedInput('password')}
                                onBlur={() => setFocusedInput(null)}
                                placeholder='••••••••'
                                disabled={loading}
                                aria-invalid={passwordError ? 'true' : 'false'}
                                className={`w-full pl-12 pr-12 py-3.5 border-2 rounded-xl transition-all duration-300 outline-none
                                    ${passwordError
                                        ? 'border-red-500 bg-red-50 text-red-900 placeholder-red-300 focus:ring-4 focus:ring-red-100'
                                        : focusedInput === 'password'
                                            ? 'border-indigo-600 bg-indigo-50/50 shadow-lg shadow-indigo-200/50 focus:ring-4 focus:ring-indigo-100'
                                            : 'border-gray-200 bg-white hover:border-gray-300 focus:ring-4 focus:ring-indigo-100'
                                    }
                                    disabled:bg-gray-50 disabled:cursor-not-allowed`}
                            />
                            <button
                                type='button'
                                onClick={() => setShowPassword(!showPassword)}
                                className='absolute inset-y-0 right-0 flex items-center pr-4 text-gray-400 hover:text-indigo-600 transition-colors duration-300 focus:outline-none'
                                disabled={loading}
                            >
                                {showPassword ? (
                                    <HiEyeOff className='w-5 h-5 transition-transform hover:scale-110' />
                                ) : (
                                    <HiEye className='w-5 h-5 transition-transform hover:scale-110' />
                                )}
                            </button>
                        </div>
                        {passwordError === 'required' && (
                            <p className='mt-2 text-sm text-red-600'>Ingresa tu contraseña.</p>
                        )}
                    </div>

                    {/* Submit Button */}
                    <div className='animate-slideUp' style={{ animationDelay: '0.3s' }}>
                        <Button
                            type='submit'
                            variant='gradient'
                            fullWidth
                            disabled={loading}
                            loading={loadingLogin}
                            loadingLabel='Iniciando sesión...'
                        >
                            Iniciar sesión
                        </Button>
                    </div>

                    {/* Forgot Password Link */}
                    <div className='flex justify-center animate-slideUp' style={{ animationDelay: '0.4s' }}>
                        <Link 
                            to={'/forget-password'} 
                            className='text-sm font-semibold text-indigo-600 hover:text-indigo-800 transition-colors duration-300 hover:underline'
                        >
                            ¿Tienes problemas para iniciar sesión?
                        </Link>
                    </div>

                    {/* Divider */}
                    <div className='relative my-8 animate-slideUp' style={{ animationDelay: '0.5s' }}>
                        <div className='absolute inset-0 flex items-center'>
                            <div className='w-full border-t-2 border-gray-200'></div>
                        </div>
                        <div className='relative flex justify-center text-sm'>
                            <span className='px-4 bg-white text-gray-500 font-medium'>¿No tienes cuenta?</span>
                        </div>
                    </div>

                    {/* Create Account Button */}
                    <div className='animate-slideUp' style={{ animationDelay: '0.6s' }}>
                        <ButtonLink to="/pre-registro" variant="outlineCta">
                            Crear una cuenta
                        </ButtonLink>
                    </div>
                    </form>
                </div>

                <CopyrightFooter className='flex-shrink-0 pb-6 pt-8 animate-fadeIn' style={{ animationDelay: '0.7s' }} />
            </div>

            {/* CSS Animations */}
            <style jsx>{`
                @keyframes fadeIn {
                    from {
                        opacity: 0;
                    }
                    to {
                        opacity: 1;
                    }
                }

                @keyframes slideDown {
                    from {
                        opacity: 0;
                        transform: translateY(-20px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                @keyframes slideUp {
                    from {
                        opacity: 0;
                        transform: translateY(20px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                .animate-fadeIn {
                    animation: fadeIn 0.6s ease-out forwards;
                }

                .animate-slideDown {
                    animation: slideDown 0.6s ease-out forwards;
                }

                .animate-slideUp {
                    animation: slideUp 0.6s ease-out forwards;
                    opacity: 0;
                    animation-fill-mode: forwards;
                }
            `}</style>
        </div>
    );
};

